/**
 * AdminSettings — runtime feature flags + app config, editable without a deploy.
 * Booleans are toggles; strings/numbers are inline-editable fields. Changes are
 * super-admin only (server-enforced) and audit-logged.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Switch, TextField, Stack, Divider, Chip,
  CircularProgress, Snackbar, Alert, useTheme, alpha, InputAdornment, IconButton,
} from '@mui/material';
import { ToggleOn, Tune, Check } from '@mui/icons-material';
import adminService from '../../services/adminService';
import authService from '../../services/authService';
import { PageHeader, panelSx } from '../ui';

const CATEGORY_META = {
  features: { title: 'Feature Flags', icon: <ToggleOn />, blurb: 'Turn parts of the product on or off instantly.' },
  system: { title: 'System', icon: <Tune />, blurb: 'Operational controls.' },
  general: { title: 'General', icon: <Tune />, blurb: 'Business configuration.' },
};

const AdminSettings = () => {
  const theme = useTheme();
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // key -> pending string/number value
  const [savingKey, setSavingKey] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isSuperAdmin = (authService.getStoredUser()?.role) === 'super_admin';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminService.getSettings();
      setGroups(res.settings || {});
    } catch {
      setGroups({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key, value) => {
    try {
      setSavingKey(key);
      const res = await adminService.updateSetting(key, value);
      // Reflect the coerced value from the server.
      setGroups((prev) => {
        const next = { ...prev };
        for (const cat of Object.keys(next)) {
          next[cat] = next[cat].map((s) => (s.key === key ? { ...s, value: res.setting?.value ?? value } : s));
        }
        return next;
      });
      setSnackbar({ open: true, message: `Updated ${key.replace(/_/g, ' ')}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Update failed (super-admin only)', severity: 'error' });
      load(); // revert optimistic UI
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const categories = Object.keys(groups);

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Feature flags and configuration, live without a deploy." />

      {!isSuperAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>You can view settings. Changing them requires a super-admin.</Alert>
      )}

      <Grid container spacing={3}>
        {categories.map((cat) => {
          const meta = CATEGORY_META[cat] || { title: cat, icon: <Tune />, blurb: '' };
          const items = groups[cat] || [];
          return (
            <Grid item xs={12} md={6} key={cat}>
              <Paper elevation={0} sx={panelSx(theme)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                  <Box sx={{ color: theme.palette.primary.main }}>{meta.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>{meta.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{meta.blurb}</Typography>
                <Divider sx={{ mb: 1 }} />
                <Stack divider={<Divider flexItem />}>
                  {items.map((s) => (
                    <Box key={s.key} sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                          {s.key.replace(/_/g, ' ')}
                        </Typography>
                        {s.description && (
                          <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                        )}
                      </Box>

                      {s.value_type === 'boolean' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={s.value ? 'On' : 'Off'}
                            sx={{
                              fontWeight: 700,
                              bgcolor: s.value ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.text.disabled, 0.12),
                              color: s.value ? theme.palette.success.dark : theme.palette.text.secondary,
                            }}
                          />
                          <Switch
                            checked={Boolean(s.value)}
                            disabled={!isSuperAdmin || savingKey === s.key}
                            onChange={(e) => save(s.key, e.target.checked)}
                          />
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          type={s.value_type === 'number' ? 'number' : 'text'}
                          defaultValue={s.value}
                          disabled={!isSuperAdmin}
                          onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                          sx={{ width: 200 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  disabled={!isSuperAdmin || savingKey === s.key || drafts[s.key] == null}
                                  onClick={() => save(s.key, drafts[s.key])}
                                  title="Save"
                                >
                                  {savingKey === s.key ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSettings;
